import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Icon, IconButton, InputAdornment, Paper, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import { ListParams } from '../../models/listParams';

import OverzichtActionMenu from './OverzichtActionMenu';
import FilterPortfolios from './FilterPortfolios';

import { PortfolioService, portfolioService } from '../../services/portfolio.service';
import NoActiveOrganisatieMessage from '../controls/NoActiveOrganisatieMessage';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';
import { userManagementService } from '../../services/user-management.service';
import { contentService } from '../../services/content.service';
import { LOCAL_STORAGE_KEYS, pageSizeStorageKey } from '../../services/utils/localStorageKeys';
import { loadJsonFromLocalStorage, saveJsonToLocalStorage } from '../../services/utils/localStorageState';
import { usePersistedPageSize } from '../../hooks/usePersistedPageSize';
import { messages } from '../../content';


interface OverzichtPortfoliosProps {
  planId?: string;
  refreshKey?: number;
}

interface StoredAfstemmingsdossierFilterState {
  selectedSearch: string;
  statusFilter: string[];
  myPlansFilter: string[];
  myOrganisatieFilter: string[];
  organizationFilter: string[];
  consortiumFilter: string[];
  sortBy: string;
  sortDirection: string;
}

const normalizeStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const getStoredAfstemmingsdossierFilterState = (): StoredAfstemmingsdossierFilterState => {
  const fallback: StoredAfstemmingsdossierFilterState = {
    selectedSearch: '',
    statusFilter: [],
    myPlansFilter: [],
    myOrganisatieFilter: [],
    organizationFilter: [],
    consortiumFilter: [],
    sortBy: '',
    sortDirection: '',
  };

  const hasNewKey = typeof window !== 'undefined' && localStorage.getItem(LOCAL_STORAGE_KEYS.AFSTEMMINGSDOSSIERS_FILTERS) !== null;
  const storageKey = hasNewKey ? LOCAL_STORAGE_KEYS.AFSTEMMINGSDOSSIERS_FILTERS : 'afstemmingsdossiers.filters.v1';

  return loadJsonFromLocalStorage<StoredAfstemmingsdossierFilterState>(
    storageKey,
    fallback,
    (parsed) => ({
      selectedSearch: typeof parsed?.selectedSearch === 'string' ? parsed.selectedSearch : '',
      statusFilter: normalizeStringArray(parsed?.statusFilter),
      myPlansFilter: normalizeStringArray(parsed?.myPlansFilter),
      myOrganisatieFilter: normalizeStringArray(parsed?.myOrganisatieFilter),
      organizationFilter: normalizeStringArray(parsed?.organizationFilter),
      consortiumFilter: normalizeStringArray(parsed?.consortiumFilter),
      sortBy: typeof parsed?.sortBy === 'string' ? parsed.sortBy : '',
      sortDirection: typeof parsed?.sortDirection === 'string' ? parsed.sortDirection : '',
    }),
  );
};

// CQL-waarden zijn enkel-quoted, dus een quote in de waarde wordt verdubbeld.
const escapeCqlValue = (value: string): string => String(value).replace(/'/g, "''");

const buildCqlClause = (field: string, values: string[]): string => {
  if (values.length === 1) {
    return `${field} = '${escapeCqlValue(values[0])}'`;
  }

  return `${field} IN (${values.map((value) => `'${escapeCqlValue(value)}'`).join(', ')})`;
};

interface PortfolioFilterSelection {
  mijnOrganisatieId?: string | null;
  statusFilter: string[];
  myPlansFilter: string[];
  myOrganisatieFilter: string[];
  organizationFilter?: string[];
  consortiumFilter?: string[];
}

const buildPortfolioCqlFilter = (selection: PortfolioFilterSelection): string | undefined => {
  const filterClauses: string[] = [];

  if (selection.statusFilter.length > 0) {
    filterClauses.push(buildCqlClause('AfstemmingsStatus', selection.statusFilter));
  }

  if (selection.mijnOrganisatieId && selection.myPlansFilter.length > 0) {
    filterClauses.push(
      `EXISTS(Plans, OrganizationId = '${escapeCqlValue(selection.mijnOrganisatieId)}' AND ${buildCqlClause('Status', selection.myPlansFilter)})`
    );
  }

  if (selection.mijnOrganisatieId && selection.myOrganisatieFilter.length > 0) {
    filterClauses.push(
      `EXISTS(Organizations, OrganizationId = '${escapeCqlValue(selection.mijnOrganisatieId)}' AND ${buildCqlClause('Status', selection.myOrganisatieFilter)})`
    );
  }

  if (selection.organizationFilter && selection.organizationFilter.length > 0) {
    filterClauses.push(`EXISTS(Organizations, ${buildCqlClause('OrganizationId', selection.organizationFilter)})`);
  }

  if (selection.consortiumFilter && selection.consortiumFilter.length > 0) {
    filterClauses.push(`EXISTS(Consortiums, ${buildCqlClause('ConsortiumId', selection.consortiumFilter)})`);
  }

  return filterClauses.length > 0 ? filterClauses.join(' AND ') : undefined;
};

const getMyOrganizationStatus = (row: any, mijnOrganisatieId?: string | null): string | null => {
  if (typeof row?.myOrganizationStatus === 'string' && row.myOrganizationStatus) {
    return row.myOrganizationStatus;
  }

  const organizations = Array.isArray(row?.organizations) ? row.organizations : [];
  const eigenOrganisatie = organizations.find((item: any) => item?.organizationId === mijnOrganisatieId);

  return eigenOrganisatie?.status ?? null;
};

const OverzichtPortfolios: React.FC<OverzichtPortfoliosProps> = ({ 
  planId,
  refreshKey
}) => {
  const navigate = useNavigate();
  const initialFilterStateRef = useRef<StoredAfstemmingsdossierFilterState>(getStoredAfstemmingsdossierFilterState());
  const [mijnOrganisatie] = useState<MijnOrganisatieListItem | null>(() =>
    userManagementService.getStoredOrganisatie()
  );
  const [portfolios, setPortfolios] = useState<any[]>([]);
  
  const [pageIndex, setPageIndex] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize(pageSizeStorageKey('afstemmingsdossiers'));
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>(initialFilterStateRef.current.sortBy);
  const [sortDirection, setSortDirection] = useState<string>(initialFilterStateRef.current.sortDirection);
  const [searchInput, setSearchInput] = useState<string>(initialFilterStateRef.current.selectedSearch);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialFilterStateRef.current.selectedSearch);
  const [statusFilter, setStatusFilter] = useState<string[]>(initialFilterStateRef.current.statusFilter);
  const [myPlansFilter, setMyPlansFilter] = useState<string[]>(initialFilterStateRef.current.myPlansFilter);
  const [myOrganisatieFilter, setMyOrganisatieFilter] = useState<string[]>(initialFilterStateRef.current.myOrganisatieFilter);
  const [organizationFilter, setOrganizationFilter] = useState<string[]>(initialFilterStateRef.current.organizationFilter);
  const [consortiumFilter, setConsortiumFilter] = useState<string[]>(initialFilterStateRef.current.consortiumFilter);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const mijnOrganisatieId = mijnOrganisatie?.id;
  const activeFilterCount =
    statusFilter.length + myPlansFilter.length + myOrganisatieFilter.length + organizationFilter.length + consortiumFilter.length;

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    saveJsonToLocalStorage(LOCAL_STORAGE_KEYS.AFSTEMMINGSDOSSIERS_FILTERS, {
      selectedSearch: searchInput,
      statusFilter,
      myPlansFilter,
      myOrganisatieFilter,
      organizationFilter,
      consortiumFilter,
      sortBy,
      sortDirection,
    });
  }, [searchInput, statusFilter, myPlansFilter, myOrganisatieFilter, organizationFilter, consortiumFilter, sortBy, sortDirection]);

  const fetchPortfolios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ListParams = {
        _offset: pageIndex * pageSize,
        _limit: pageSize,
        _sort: sortBy && sortDirection ? `${sortBy} ${sortDirection}` : undefined,
      };

      if (!planId) {
        params._search = debouncedSearch || undefined;
        params._filter = buildPortfolioCqlFilter({
          mijnOrganisatieId,
          statusFilter,
          myPlansFilter,
          myOrganisatieFilter,
          organizationFilter,
          consortiumFilter,
        });
      }

      let data = [];
      if (planId) {
        data = await portfolioService.getPortfolioOverviewByPlan(planId, params);
      }
      else {
        data = await portfolioService.getPortfolios(params);
      }

      setPortfolios(data.items);
      setTotalCount(data.count);
    }
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [navigate, planId, pageIndex, pageSize, sortBy, sortDirection, debouncedSearch, statusFilter, myPlansFilter, myOrganisatieFilter, organizationFilter, consortiumFilter, mijnOrganisatieId]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPageIndex(0);
      setDebouncedSearch(value);
    }, 450);
  };

  const handleStatusChange = (values: string[]) => {
    setStatusFilter(values);
    setPageIndex(0);
  };

  const handleMyPlansChange = (values: string[]) => {
    setMyPlansFilter(values);
    setPageIndex(0);
  };

  const handleMyOrganisatieChange = (values: string[]) => {
    setMyOrganisatieFilter(values);
    setPageIndex(0);
  };

  const handleOrganizationChange = (values: string[]) => {
    setOrganizationFilter(values);
    setPageIndex(0);
  };

  const handleConsortiumChange = (values: string[]) => {
    setConsortiumFilter(values);
    setPageIndex(0);
  };

  const handleClearAllFilters = () => {
    setStatusFilter([]);
    setMyPlansFilter([]);
    setMyOrganisatieFilter([]);
    setOrganizationFilter([]);
    setConsortiumFilter([]);
    setPageIndex(0);
  };

  useEffect(() => {
    setReady(true);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchPortfolios();
  }, [ready, fetchPortfolios, refreshKey]);

  const gridColumns = useMemo<(GridColDef<any> | GridActionsColDef<any>)[]>(() => {
    const baseColumns: (GridColDef<any> | GridActionsColDef<any>)[] = [
      {
        field: 'name',
        headerName: 'Naam dossier',
        flex: 3,
        renderCell: (params: any) => {
          const hasAccess = params.row?.hasAccess !== false;

          if (!hasAccess) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, paddingTop: 6, paddingBottom: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{params.row.name}</span>
                  <Tooltip title={messages.NO_ACCESS_TO_PORTFOLIO} arrow>
                    <Icon fontSize="small" sx={{ color: 'text.secondary' }}>block</Icon>
                  </Tooltip>
                </span>
              </div>
            );
          }

          return (
            <Link className="lnk" to={`/dossiers/${params.row.id}`}>
              {params.row.name}
            </Link>
          );
        },
      }
    ];

    const afstemmingsStatusColumn: GridColDef<any> = {
      field: 'afstemmingsStatus',
      headerName: 'Afstemming',
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const statusOptions = PortfolioService.STATUS_OPTIONS;
        const status = params.row.afstemmingsStatus;
        const statusOption = statusOptions.find(option => option.id === status);
        const icon = statusOption ? statusOption.icon : '';
        const tooltipText = statusOption ? `Afstemmingsstatus: ${statusOption.name} - ${statusOption.tooltip}` : 'Geen status beschikbaar';

        return (
          <Tooltip title={tooltipText} placement="right" arrow>
            <span className={`afstemmings-status-icon ${status}`}>
              <span className="material-icons material-symbols-rounded">{icon}</span>
            </span>
          </Tooltip>
        );
      }
    };

    baseColumns.push(afstemmingsStatusColumn);

    const myOrganisatieColumn: GridColDef<any> = {
      field: 'myOrganizationStatus',
      headerName: 'Deelname mijn organisatie',
      renderHeader: () => (
        <Tooltip
          title="De deelnamestatus van jouw organisatie in dit afstemmingsdossier"
          arrow
        >
          <span>Deelname mijn organisatie</span>
        </Tooltip>
      ),
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const hasAccess = params.row?.hasAccess !== false;

        if (!hasAccess) {
          return null;
        }

        const status = getMyOrganizationStatus(params.row, mijnOrganisatieId);

        if (!status) {
          return (
            <Tooltip title="Jouw organisatie is nog niet uitgenodigd voor dit afstemmingsdossier" arrow>
              <span>-</span>
            </Tooltip>
          );
        }

        const isPending = status === 'pending';
        const isAccepted = status === 'accepted';
        const label = isPending
          ? 'Wacht op reactie'
          : isAccepted
            ? 'Actief'
            : contentService.getInclusionRequestStatusLabel(status);
        const tooltipText = isPending
          ? 'Wacht op reactie. Jouw organisatie heeft nog niet gereageerd op de uitnodiging voor dit afstemmingsdossier'
          : isAccepted
            ? 'Jouw organisatie neemt actief deel aan dit afstemmingsdossier'
            : `Deelname van jouw organisatie: ${label}`;

        return (
          <Tooltip title={tooltipText} arrow>
            <span className={`afstemmings-status-icon ${isPending ? 'in_afstemming' : isAccepted ? 'afgestemd' : 'nieuw'}`}>
              <span className="material-icons material-symbols-rounded">
                {isPending ? 'pause' : isAccepted ? 'check' : 'info'}
              </span>
            </span>
          </Tooltip>
        );
      }
    };

    if (planId) {
      baseColumns.push(myOrganisatieColumn);

      baseColumns.push({
        field: "approvalPending",
        headerName: "Deelname voor dit plan geaccepteerd?",
        renderHeader: () => (
          <Tooltip
            title="Geeft aan of deelname voor dit plan geaccepteerd is in dit afstemmingsdossier"
            arrow
          >
            <span>Deelname voor dit plan geaccepteerd?</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const hasAccess = params.row?.hasAccess !== false;

          if (!hasAccess) {
            return null;
          }

          return <span>{params.row.approvalPending ? "Nee" : "Ja"}</span>;
        }
      });
    }

    if (!planId) {
      baseColumns.push({
        field: "myPlansPending",
        headerName: "Deelname mijn plannen",
        renderHeader: () => (
          <Tooltip
            title="Geeft aan of er nog plannen van jouw organisatie wachten op een reactie om deel te nemen"
            arrow
          >
            <span>Deelname mijn plannen</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const isPending = params.row?.myPlansPending === true;
          const mijnPlannen = Array.isArray(params.row?.plans)
            ? params.row.plans.filter((plan: any) => plan?.organizationId === mijnOrganisatieId)
            : [];
          const heeftGeenPlannen = mijnPlannen.length === 0;
          const tooltipText = heeftGeenPlannen
            ? 'Jouw organisatie heeft geen plannen in dit afstemmingsdossier'
            : isPending
              ? 'Minstens 1 plan van jouw organisatie wacht nog op een reactie om deel te nemen'
              : 'Er zijn geen plannen van jouw organisatie in dit afstemmingsdossier die de status uitgenodigd hebben';

          return (
            <Tooltip title={tooltipText} arrow>
              <span className={`afstemmings-status-icon ${heeftGeenPlannen ? 'geen_plannen' : isPending ? 'in_afstemming' : 'afgestemd'}`}>
                <span className={heeftGeenPlannen ? 'material-symbols-outlined' : 'material-icons material-symbols-rounded'}>
                  {heeftGeenPlannen ? 'counter_0' : isPending ? 'pause' : 'check'}
                </span>
              </span>
            </Tooltip>
          );
        }
      });

      baseColumns.push(myOrganisatieColumn);

      baseColumns.push({
        field: 'organizations',
        headerName: 'Organisaties',
        renderHeader: () => (
          <Tooltip
            title="Aantal organisaties dat actief deelneemt aan dit afstemmingsdossier"
            arrow
          >
            <span>Actieve organisaties</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const organizations = Array.isArray(params.row.organizations) ? params.row.organizations : [];
          const actieveOrganisaties = organizations.filter(
            (item: any) => (item?.status ?? '').toLowerCase() === 'accepted'
          );
          const organisatieNamen = actieveOrganisaties
            .map((item: any) => item?.displayName)
            .filter(Boolean)
            .sort((a: string, b: string) => a.localeCompare(b, 'nl-NL'))
            .join(', ');
          const tooltipText = organisatieNamen
            ? `Actieve organisaties: ${organisatieNamen}`
            : 'Geen actieve organisaties';

          return (
            <Tooltip title={tooltipText} arrow>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Badge
                  badgeContent={actieveOrganisaties.length}
                  color={actieveOrganisaties.length === 0 ? 'secondary' : 'primary'}
                  showZero
                  sx={{ mr: 1 }}
                >
                  <Icon fontSize="small">business</Icon>
                </Badge>
              </span>
            </Tooltip>
          );
        }
      });

      baseColumns.push({ 
        field: 'planCount', 
        headerName: 'Aantal plannen actief',
        renderHeader: () => (
          <Tooltip
            title="Het totaal aantal plannen die actief deelnemen in het afstemmingsdossier"
            arrow
          >
            <span>Aantal plannen actief</span>
          </Tooltip>
        ), 
        flex: 2,
        sortable: false,
        filterable: false 
      });

      baseColumns.push({
        field: 'consortiums',
        headerName: 'Samenwerkingsverbanden',
        renderHeader: () => (
          <Tooltip
            title="Aantal gekoppelde samenwerkingsverbanden binnen dit afstemmingsdossier"
            arrow
          >
            <span>Samenwerkingsverbanden</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const consortiums = Array.isArray(params.row.consortiums) ? params.row.consortiums : [];
          const consortiumNames = consortiums
            .map((item: any) => item?.name)
            .filter(Boolean)
            .sort((a: string, b: string) => a.localeCompare(b, 'nl-NL'))
            .join(', ');
          const tooltipText = consortiumNames ? `Samenwerkingsverbanden: ${consortiumNames}` : 'Geen samenwerkingsverbanden gekoppeld';

          return (
            <Tooltip title={tooltipText} arrow>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Badge
                  badgeContent={consortiums.length}
                  color={consortiums.length === 0 ? 'secondary' : 'primary'}
                  showZero
                  sx={{ mr: 1 }}
                >
                  <Icon fontSize="small">groups</Icon>
                </Badge>
              </span>
            </Tooltip>
          );
        }
      });

      const columnsByField = new Map(baseColumns.map((column) => [column.field, column]));
      const columnOrder = [
        'name',
        'afstemmingsStatus',
        'myOrganizationStatus',
        'organizations',
        'myPlansPending',
        'planCount',
        'consortiums',
      ];

      baseColumns.splice(
        0,
        baseColumns.length,
        ...columnOrder.map((field) => columnsByField.get(field)).filter(Boolean) as (GridColDef<any> | GridActionsColDef<any>)[]
      );
    }

    // baseColumns.push({
    //   field: 'isActiveDossier',
    //   headerName: 'Dossier is actief',
    //   flex: 2,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params: GridRenderCellParams<any>) => (
    //     <span>{params.row.planCount > 1 ? 'Ja' : 'Nee'}</span>
    //   )
    // });

    baseColumns.push({
      field: 'actions',
      headerName: 'Acties',
      renderHeader: () => <></>,
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const hasAccess = params.row?.hasAccess !== false;
        // const heeftOpenstaandePlannen = params.row?.myPlansPending ?? params.row?.approvalPending;
        const mijnOrganisatieStatus = (getMyOrganizationStatus(params.row, mijnOrganisatieId) ?? '').toLowerCase();
        const isUitgenodigd = mijnOrganisatieStatus === 'pending';

        // if (!hasAccess || heeftOpenstaandePlannen || isUitgenodigd) {
        if (!hasAccess || isUitgenodigd) {
          return null;
        }

        return <OverzichtActionMenu params={params} onPortfolioUpdate={fetchPortfolios} />;
      }
    });
    
    return baseColumns;
  }, [planId, fetchPortfolios, mijnOrganisatieId]);

  const onChangePagination = ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPageIndex(page);
    setPageSize(pageSize);
  };

  const handleSortChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      setSortBy(field);
      setSortDirection(sort || "asc");
    }
    else {
      setSortBy("");
      setSortDirection("");
    }
  };

  if (!mijnOrganisatie) {
    return (
      <>
        <Paper>
          <NoActiveOrganisatieMessage />
        </Paper>
      </>
    )
  }

  const dataGrid = (
    <div className="grid-responsive">
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={portfolios}
          columns={gridColumns}
          getRowId={(row) => row.id || ''}
          sortModel={sortBy && sortDirection ? [{ field: sortBy, sort: sortDirection as 'asc' | 'desc' }] : []}
          localeText={{
            noRowsLabel: messages.NO_PORTFOLIOS,
            MuiTablePagination: {
              labelRowsPerPage: 'Dossiers per pagina:',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) => `${from}-${to} van ${count}`,
            },
          }}
          disableColumnFilter
          disableRowSelectionOnClick
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                page: pageIndex,
                pageSize: pageSize
              }
            }
          }}
          onPaginationModelChange={onChangePagination}
          onSortModelChange={handleSortChange}
          sortingMode="server"
          sx={{ border: 0 }}
        />
      </Paper>
    </div>
  );

  if (planId) {
    return (
      <>
        {dataGrid}
        {loading && <ProgressBar />}
        {error && <ErrorMessage message={error} />}
      </>
    );
  }

  return (
    <div className="portfolio-with-filter-panel">
      <div className={`filter-panel-left${filterPanelOpen ? '' : ' filter-panel-left--closed'}`}>
        <FilterPortfolios
          statusFilter={statusFilter}
          myPlansFilter={myPlansFilter}
          myOrganisatieFilter={myOrganisatieFilter}
          organizationFilter={organizationFilter}
          consortiumFilter={consortiumFilter}
          onChangeStatus={handleStatusChange}
          onChangeMyPlans={handleMyPlansChange}
          onChangeMyOrganisatie={handleMyOrganisatieChange}
          onChangeOrganization={handleOrganizationChange}
          onChangeConsortium={handleConsortiumChange}
          onClearAll={handleClearAllFilters}
          onClose={() => setFilterPanelOpen(false)}
        />
      </div>

      <div className="portfolio-content-area">
        <div className="sidebar-search-bar">
          <TextField
            label="Zoeken in afstemmingsdossiers"
            variant="filled"
            placeholder="Zoeken op naam of omschrijving"
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (debounceTimer.current) clearTimeout(debounceTimer.current);
                      setSearchInput('');
                      setDebouncedSearch('');
                      setPageIndex(0);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Tooltip
            title={activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} actief` : ''}
            disableHoverListener={activeFilterCount === 0}
          >
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              overlap="rectangular"
              sx={{ '& .MuiBadge-badge': { top: 6, right: 6 } }}
            >
              <Tooltip title={filterPanelOpen ? 'Sluit het filter' : 'Open filter om uitgebreid te zoeken in afstemmingsdossiers'}>
                <IconButton size="large" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
                  <Icon>{filterPanelOpen ? 'filter_list_off' : 'filter_list'}</Icon>
                </IconButton>
              </Tooltip>
            </Badge>
          </Tooltip>
        </div>

        {dataGrid}

        {loading && <ProgressBar />}
        {error && <ErrorMessage message={error} />}
      </div>
    </div>
  );
};

export default OverzichtPortfolios;
